/**
 * useCollectionDocuments Hook
 * React Query hook for fetching documents belonging to a specific collection
 */

import { useQuery } from "@tanstack/react-query";

import type { CollectionDocument } from "../types/admin";

const API_BASE = "/api/collection";

/**
 * Fetch documents for a specific collection
 */
async function fetchCollectionDocuments(collectionId: number): Promise<CollectionDocument[]> {
  const response = await fetch(`${API_BASE}/${collectionId}/documents`, {
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const data: Array<{
    documentId: number;
    collectionId: number;
    sourceType: "web" | "google-drive";
    availability: boolean;
    title: string;
    category: string;
    documentDate?: string | null;
    governanceBody?: "groepsraad" | "werkoverleg" | "bestuur" | null;
    issuingAuthority?: string | null;
    authorityScope?:
      | "internal"
      | "scouting-national"
      | "municipality"
      | "province"
      | "national-government"
      | "tax-authority"
      | "ministry"
      | "other"
      | "unknown"
      | null;
    metadataLocked: boolean;
    conversionStatus: "pending" | "completed" | "error";
    conversionError?: string | null;
    extractionStatus: "pending" | "completed" | "error";
    extractionError?: string | null;
    priority: number;
    downloadedAt?: string | null;
    convertedAt?: string | null;
    extractedAt?: string | null;
    metadata?: Record<string, unknown> | null;
    source: {
      name: string;
      mimeType: string;
      url: string;
    };
  }> = await response.json();

  return data.map((document) => ({
    documentId: document.documentId,
    collectionId: document.collectionId,
    sourceType: document.sourceType,
    availability: document.availability,
    name: document.source.name,
    mimeType: document.source.mimeType,
    title: document.title,
    category: document.category,
    documentDate: document.documentDate ?? null,
    governanceBody: document.governanceBody ?? null,
    issuingAuthority: document.issuingAuthority ?? null,
    authorityScope: document.authorityScope ?? null,
    metadataLocked: document.metadataLocked,
    conversionStatus: document.conversionStatus,
    conversionError: document.conversionError ?? null,
    extractionStatus: document.extractionStatus,
    extractionError: document.extractionError ?? null,
    priority: document.priority,
    downloadedAt: document.downloadedAt ?? null,
    convertedAt: document.convertedAt ?? null,
    extractedAt: document.extractedAt ?? null,
    metadata: document.metadata ?? null,
    sourceUrl: document.source.url,
  }));
}

/**
 * Hook to fetch documents for a collection
 * Only fetches when enabled is true (lazy loading for expand/collapse)
 */
export function useCollectionDocuments(collectionId: number, enabled: boolean = false) {
  return useQuery({
    queryKey: ["collectionDocuments", collectionId],
    queryFn: () => fetchCollectionDocuments(collectionId),
    enabled: Number.isFinite(collectionId) && enabled,
    staleTime: 30000, // Consider data fresh for 30 seconds
  });
}
